// ─────────────────────────────────────────────────────────────────────────────
// RecentShows, the band member's "Recent shows · tap for media" strip. Sits in
// the SAME slot as Biggest movers (and reuses its .movers/.mover card styling) so
// it drops in natively: for the band-member view it REPLACES Biggest movers. Each
// card is a recent past show (flyer thumb + date·venue·city) that opens the
// post-show media link when one exists. Source: band-member-wiring.js past-shows.
// ─────────────────────────────────────────────────────────────────────────────

import type { JSX, CSSProperties } from 'react';
import { useBandMemberSpotlight } from '../../lib';
import type { BandMemberShowTile } from '../../lib';

function formatShortDate(iso: string): string {
  if (!iso) return '';
  const d = new Date(iso + 'T00:00:00');
  if (isNaN(d.getTime())) return iso;
  return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
}

function thumbStyle(s: BandMemberShowTile): CSSProperties {
  if (s.flyerUrl) {
    const safe = s.flyerUrl.replace(/["'()\\\s]/g, (c) => encodeURIComponent(c));
    return {
      backgroundColor: '#000',
      backgroundImage: "url('" + safe + "')",
      backgroundSize: 'cover',
      backgroundPosition: 'center',
      backgroundRepeat: 'no-repeat',
    };
  }
  return {};
}

export function RecentShows(): JSX.Element {
  const { data } = useBandMemberSpotlight();
  const shows = data.pastShows;
  const hasData = shows.length > 0;

  const open = (url: string): void => { if (url) window.open(url, '_blank', 'noopener'); };

  return (
    <section aria-labelledby="recent-shows-label">
      <div className="section-label">
        <h3 className="section-label__title" id="recent-shows-label">
          Recent shows
        </h3>
        <span className="section-label__meta">TAP FOR MEDIA</span>
      </div>
      <ul className="movers">
        {hasData
          ? shows.map((s, i) => {
              const hasMedia = !!s.mediaLink;
              const sub = formatShortDate(s.date) + (s.city ? ' · ' + s.city : '');
              return (
                <li
                  className={'mover' + (hasMedia ? ' mover--link' : '')}
                  key={s.id || i}
                  onClick={hasMedia ? () => open(s.mediaLink) : undefined}
                  role={hasMedia ? 'button' : undefined}
                  tabIndex={hasMedia ? 0 : undefined}
                  onKeyDown={
                    hasMedia ? (e) => { if (e.key === 'Enter' || e.key === ' ') open(s.mediaLink); } : undefined
                  }
                >
                  <div className="mover__thumb" style={thumbStyle(s)}>
                    {!s.flyerUrl && <span className="mover__rank">#{i + 1}</span>}
                  </div>
                  <div className="mover__body">
                    <span className="mover__plat" title={s.venue}>{s.venue}</span>
                    <span className="mover__src">{sub}</span>
                    <span className={'mover__change ' + (hasMedia ? 'up' : 'neutral')}>
                      {hasMedia ? 'Click for media →' : 'No media yet'}
                    </span>
                  </div>
                </li>
              );
            })
          : [1, 2, 3, 4, 5].map((n) => (
              <li className="mover mover--empty" key={n}>
                <div className="mover__thumb">
                  <span className="mover__rank">#{n}</span>
                </div>
                <div className="mover__body">
                  <span className="mover__plat">-</span>
                  <span className="mover__change">No past shows yet</span>
                </div>
              </li>
            ))}
      </ul>
    </section>
  );
}
